import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import Picker from '@wojtekmaj/react-daterange-picker/dist/entry.nostyle'
import { CalendarProps } from 'react-calendar'
import styles from './DateRangePicker.module.scss'
import cl from 'classnames'

import './Calendar.css'
import './DateRangePicker.css'
import './react-calendar.css'
import { DateRangePickerProps, PeriodValue } from './DateRangePicker.types'
import moment from 'moment'
import { parsePeriodToDates } from './DateRangePicker.utils'

const VIEWPORT_PADDING = 8
const GAP = 4

const DateRangePicker = (props: DateRangePickerProps) => {
  const {
    visible,
    setVisibility,
    initialValue,
    onSelect,
    className = '',
    anchorRef
  } = props

  const [value, setValue] = useState<PeriodValue>(initialValue)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const popoverRef = useRef<HTMLDivElement>(null)

  const handleOnChange = (dates: Date[]) => {
    const period: PeriodValue = dates
      ? {
        start: moment(dates[0]),
        end: moment(dates[1])
      }
      : null
    setValue(period)
    onSelect(period)
  }

  const updatePosition = useCallback(() => {
    const popoverEl = popoverRef.current
    if (!popoverEl) return

    const popoverWidth = popoverEl.offsetWidth
    const popoverHeight = popoverEl.offsetHeight
    const anchorEl = anchorRef?.current

    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect()

      let left = rect.left
      left = Math.max(
        VIEWPORT_PADDING,
        Math.min(left, window.innerWidth - popoverWidth - VIEWPORT_PADDING)
      )

      let top = rect.bottom + GAP
      if (
        top + popoverHeight > window.innerHeight - VIEWPORT_PADDING &&
        rect.top - popoverHeight - GAP >= VIEWPORT_PADDING
      ) {
        top = rect.top - popoverHeight - GAP
      }
      top = Math.max(
        VIEWPORT_PADDING,
        Math.min(top, window.innerHeight - popoverHeight - VIEWPORT_PADDING)
      )

      setPosition({ top, left })
    } else {
      setPosition({
        top: Math.max(
          VIEWPORT_PADDING,
          (window.innerHeight - popoverHeight) / 2
        ),
        left: Math.max(
          VIEWPORT_PADDING,
          (window.innerWidth - popoverWidth) / 2
        )
      })
    }
  }, [anchorRef])

  useEffect(() => {
    if (!visible) return

    updatePosition()
    requestAnimationFrame(() => updatePosition())

    const handleScroll = () => updatePosition()
    const handleResize = () => updatePosition()

    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [visible, updatePosition])

  useEffect(() => {
    if (!visible) return

    const handleClickOutside = (e: MouseEvent) => {
      const anchorEl = anchorRef?.current
      const popoverEl = popoverRef.current
      const target = e.target as Node

      if (anchorEl?.contains(target) || popoverEl?.contains(target)) return

      setVisibility(false)
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [visible, anchorRef, setVisibility])

  const calendarProps: CalendarProps = {
    value: parsePeriodToDates(value),
    onChange: handleOnChange as any,
    showDoubleView: true
  }

  if (!visible) return null

  const content = (
    <div
      ref={popoverRef}
      className={cl(styles.container, { [styles.visible]: visible }, className)}
      style={{ top: position.top, left: position.left }}
    >
      <div
        className={styles.wrapper}
        onMouseLeave={() => setTimeout(() => setVisibility(false), 300)}
      >
        <Picker autoFocus={true} {...calendarProps} />
      </div>
    </div>
  )

  return ReactDOM.createPortal(content, document.body)
}

export default DateRangePicker
